import { useFormik } from "formik";
import { FC, useRef, useState } from "react";
import Swal from "sweetalert2";
import useAccessToken from "../../../hooks/AccessToken";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { useIntl } from "react-intl";
import clsx from "clsx";
import Select from 'react-select'
import useUser from "../../../hooks/User";
import { convertHtmlToWhatsappFormat, convertMinutesToSeconds, convertSecondsToMinutes, convertWhatsappToHtml, customNumberFormatString } from "../../../functions/general";
import { putSettingChat } from "../../../api/ChatCRUD";
import ReactQuill from "react-quill";

interface Props {
}

const StudioChat: FC<Props> = () => {
    const intl = useIntl()
    const user = useUser()
    const token = useAccessToken()
    const { settings, setSettings } = useSettingsStore()
    const [loadingPreferences, setLoadingPreferences] = useState(false)
    const quillRef = useRef<ReactQuill>(null)

    const optionVariable = [
        {
            value: 'name',
            label: intl.formatMessage({ id: 'FORM.LABEL.NAME' })
        }
    ]

    const quilFormats = [
        'bold',
        'italic',
        'underline',
        'strike',
    ]

    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike',],
        ],
    }

    const formikPreferences = useFormik({
        initialValues: {
            checkSessionTimeout: settings.sessionsTimeoutInSeconds === "0" ? false : true,
            sessionTimeout: settings.sessionsTimeoutInSeconds === "0" ? "" : convertSecondsToMinutes(Number(settings.sessionsTimeoutInSeconds)),
            allowAgentPreemptiveStartSessions: settings.allowAgentPreemptiveStartSessions,
            captionWhenSessionEnded: convertWhatsappToHtml([settings.captionWhenSessionEnded], 'defaultValue', true),
        },
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            let data = {
                checkSessionTimeout: values.checkSessionTimeout,
                sessionTimeout: values.sessionTimeout,
                allowAgentPreemptiveStartSessions: values.allowAgentPreemptiveStartSessions,
                captionWhenSessionEnded: convertHtmlToWhatsappFormat(values.captionWhenSessionEnded),
            }

            if (data.checkSessionTimeout && data.sessionTimeout === "") {
                Swal.fire({
                    title: intl.formatMessage({ id: "FORM.VALIDATION.MAX_SESSION_TIME" }),
                    icon: 'error',
                    confirmButtonText: 'Ok',
                    heightAuto: false
                })
                return false
            }

            if (!data.checkSessionTimeout) data.sessionTimeout = 0
            else data.sessionTimeout = convertMinutesToSeconds(Number(data.sessionTimeout))

            setLoadingPreferences(true)
            try {
                const response = await putSettingChat(data.sessionTimeout, data.allowAgentPreemptiveStartSessions, data.captionWhenSessionEnded, token)
                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: intl.formatMessage({ id: "FORM.VALIDATION.SAVE_SUCCESS" }),
                        timer: 2000,
                        timerProgressBar: true
                    })
                    setSettings({
                        ...settings,
                        allowAgentPreemptiveStartSessions: data.allowAgentPreemptiveStartSessions,
                        sessionsTimeoutInSeconds: String(data.sessionTimeout),
                        captionWhenSessionEnded: convertWhatsappToHtml([data.captionWhenSessionEnded], 'defaultValue', true),
                    })
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.respons.data.message,
                    confirmButtonText: 'Ok',
                    heightAuto: false
                })
            } finally {
                setLoadingPreferences(false)
            }
        }
    })

    const handleInsertVariable = (variable: string) => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor()
            const range = editor.getSelection(true)
            editor.insertText(range.index, `{${variable}}`)
        }
    }

    return (
        <>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0 w-100 justify-content-between">
                        <h3 className="fw-bolder m-0">{intl.formatMessage({ id: "FORM.LABEL.PREFERENCES" }, { title: 'Chat' })}</h3>
                    </div>
                </div>
                <div>
                    <form onSubmit={formikPreferences.handleSubmit} noValidate>
                        <div className="card-body border-top px-9 py-9">
                            <div className="row">
                                <label className="col-md-7 col-lg-9 col-xl-9 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer">
                                    <input className="form-check-input me-3" type="checkbox" {...formikPreferences.getFieldProps('checkSessionTimeout')} checked={formikPreferences.values.checkSessionTimeout} onChange={(e) => formikPreferences.setFieldValue('checkSessionTimeout', e.target.checked)} />
                                    <span className="form-check-label d-flex flex-column align-items-start">
                                        <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.MAX_SESSION_TIME" })}`}</span>
                                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_MAX_SESSION_TIME" })}</span>
                                    </span>
                                </label>
                                {
                                    formikPreferences.values.checkSessionTimeout &&
                                    <div className="col-md-5 col-lg-3 col-xl-3">
                                        <div className="input-group input-group-sm">
                                            <input
                                                type='text'
                                                className={clsx(
                                                    'form-control form-control-lg',
                                                    { 'is-invalid': formikPreferences.touched.sessionTimeout && formikPreferences.errors.sessionTimeout },
                                                )}
                                                name='sessionTimeout'
                                                value={formikPreferences.values.sessionTimeout ? customNumberFormatString(formikPreferences.values.sessionTimeout.toString()) : ''}
                                                onChange={(e) => {
                                                    const numericValue = e.target.value.replace(/\D/g, '');
                                                    if (numericValue === '' || Number(numericValue) === 0) {
                                                        formikPreferences.setFieldValue('sessionTimeout', '');
                                                        return;
                                                    }
                                                    formikPreferences.setFieldValue('sessionTimeout', numericValue);
                                                }}
                                            />
                                            <span className="input-group-text">{intl.formatMessage({ id: "MINUTES" })}</span>
                                        </div>
                                        {formikPreferences.touched.sessionTimeout && formikPreferences.errors.sessionTimeout && (
                                            <div className='fv-plugins-message-container text-danger'>
                                                <span role='alert' className="text-danger">{formikPreferences.errors.sessionTimeout}</span>
                                            </div>
                                        )}
                                    </div>
                                }
                            </div>
                            <div className="separator separator-dashed my-6"></div>
                            <div className="row">
                                <label className="col-12 form-check form-switch form-check-custom form-check-solid align-items-start cursor-pointer">
                                    <input className="form-check-input me-3" type="checkbox" {...formikPreferences.getFieldProps('allowAgentPreemptiveStartSessions')} checked={formikPreferences.values.allowAgentPreemptiveStartSessions} onChange={(e) => formikPreferences.setFieldValue('allowAgentPreemptiveStartSessions', e.target.checked)} />
                                    <span className="form-check-label d-flex flex-column align-items-start">
                                        <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.AGENT_EARLY_SESSION" })}`}</span>
                                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_AGENT_EARLY_SESSION" })}</span>
                                    </span>
                                </label>
                            </div>
                            <div className="separator separator-dashed my-6"></div>
                            <div className="row">
                                <label className="col-md-7 col-lg-8 col-xl-7 form-check form-switch form-check-custom form-check-solid align-items-start">
                                    <span className="form-check-label d-flex flex-column align-items-start">
                                        <span className="fw-bolder fs-5 mb-0">{`${intl.formatMessage({ id: "FORM.LABEL.MESSAGE_SESSION_END" })}`}</span>
                                        <span className="text-muted fs-6">{intl.formatMessage({ id: "FORM.LABEL.DESC_MESSAGE_SESSION_END" })}</span>
                                    </span>
                                </label>
                                <div className="col-md-5 col-lg-4 col-xl-5" style={{ position: "relative" }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={formikPreferences.values.captionWhenSessionEnded}
                                        onChange={(content, delta, source, editor) => {
                                            formikPreferences.setFieldValue('captionWhenSessionEnded', content)
                                        }}
                                        formats={quilFormats}
                                        modules={quillModules}
                                        ref={quillRef}
                                        style={{ width: '100%' }}
                                        onBlur={() => {
                                            formikPreferences.setFieldTouched('captionWhenSessionEnded', true)
                                        }}
                                        className={clsx('link',
                                            { 'is-invalid': formikPreferences.touched.captionWhenSessionEnded && formikPreferences.errors.captionWhenSessionEnded },
                                        )}
                                    />
                                    <Select
                                        styles={{
                                            control: (baseStyles, state) => ({
                                                ...baseStyles,
                                                border: 'none',
                                                backgroundColor: 'none',
                                                width: '130px',
                                            }),
                                            option: (baseStyles, { isSelected, isFocused }) => ({
                                                ...baseStyles,
                                                backgroundColor: isSelected ? '#2684FF' : isFocused ? '#dcf2ff' : 'none',
                                                cursor: 'pointer',
                                            }),
                                            menuList: (baseStyles, state) => ({
                                                ...baseStyles,
                                                width: '130px',
                                            }),
                                            menu: (baseStyles, state) => ({
                                                ...baseStyles,
                                                width: '130px',
                                                right: 5,
                                            }),

                                            dropdownIndicator: (baseStyles, state) => ({
                                                ...baseStyles,
                                                display: 'none',
                                            }),
                                            indicatorSeparator: (baseStyles, state) => ({
                                                ...baseStyles,
                                                display: 'none',
                                            }),
                                        }}
                                        theme={(theme) => ({
                                            ...theme,
                                            colors: {
                                                ...theme.colors,
                                                primary: 'none',
                                            },
                                        })}
                                        // components={{ Option: InvoiceVariableOptions }}
                                        onChange={(value) => handleInsertVariable(value?.value ?? '')}
                                        value={null}
                                        className='react-select-styled position-absolute top-0 end-0 mt-1 me-1'
                                        classNamePrefix='react-select'
                                        options={optionVariable}
                                        placeholder={`${intl.formatMessage({ id: 'FORM.ACTION.CHOOSE' })} ${intl.formatMessage({ id: 'FORM.LABEL.VARIABLE' })}`}
                                        isSearchable={true}
                                    />
                                    {formikPreferences.touched.sessionTimeout && formikPreferences.errors.sessionTimeout && (
                                        <div className='fv-plugins-message-container text-danger'>
                                            <span role='alert' className="text-danger">{formikPreferences.errors.sessionTimeout}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end py-6 px-9">
                            <button type="submit" className={`btn btn-primary ${loadingPreferences ? "disabled" : ""}`} data-kt-indicator={loadingPreferences ? 'on' : 'off'}>
                                <span className="indicator-label">
                                    {intl.formatMessage({ id: "FORM.ACTION.SAVE" })}
                                </span>
                                <span className="indicator-progress">
                                    Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    )
}

export { StudioChat }